import PropTypes from 'prop-types'
import React, { useMemo, useEffect, useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'

import WebHook from '@/checkout-instore-legacy/components/Commons/WebHook'
import { getInstoreConfig } from '@/checkout-instore-legacy/utils/configUtils'
import {
  getOrderPayloadToEmit,
  shouldPrintOrder,
  shouldDoLocalInputInvoice,
} from '@/checkout-instore-legacy/utils/printUtils'
import { genericCriticalError } from '@/checkout-instore-legacy/utils/EventHelper'
import * as ErrorUtils from '@/checkout-instore-legacy/utils/ErrorUtils'
import { getDeviceHook } from '@/checkout-instore-legacy/order/utils'

const InvoiceHook = React.forwardRef((props, ref) => {
  const {
    order,
    device,
    sendImmediately,
    type,
    messages,
    errorNodeSelector,
    children,
    retries,
  } = props
  const { printingConfig, invoiceConfig } = getInstoreConfig()

  const [orderHook, setOrderHook] = useState(null)
  const [loadingHook, setLoadingHook] = useState(false)

  const shouldHaveHook = useMemo(() => {
    return type === 'Input'
      ? shouldDoLocalInputInvoice()
      : shouldPrintOrder(order)
  }, [order, type])

  const hasOrderHook = shouldHaveHook && !!orderHook

  const useEventHook = useMemo(() => {
    const printByBroker =
      !isEmpty(invoiceConfig) && !isNil(invoiceConfig.printByBroker)
        ? invoiceConfig.printByBroker
        : printingConfig && printingConfig.printByBroker

    return !!(device && printByBroker)
  }, [invoiceConfig, printingConfig, device])

  useEffect(() => {
    if (hasOrderHook || loadingHook) {
      return
    }

    setLoadingHook(true)

    getOrderPayloadToEmit({
      printingConfig,
      order,
      device,
      type,
      retries,
    })
      .then((newOrderHook) => {
        setOrderHook(newOrderHook)
      })
      .catch((error) => {
        genericCriticalError('order-hook-error', {
          context: 'Sale',
          error: ErrorUtils.getErrorMessage(error),
        })
      })
      .finally(() => setLoadingHook(false))
  }, [printingConfig, order, device, type, hasOrderHook, loadingHook, retries])

  const finalHook = useMemo(() => {
    return useEventHook ? getDeviceHook(orderHook) : orderHook
  }, [orderHook, useEventHook])

  return (
    hasOrderHook && (
      <div className="dib invoice-hook no-print">
        <WebHook
          ref={ref}
          sendImmediately={sendImmediately}
          hook={finalHook}
          errorNodeSelector={errorNodeSelector}
          useEventHook={useEventHook}
          messages={messages}
        >
          {children}
        </WebHook>
      </div>
    )
  )
})

InvoiceHook.defaultProps = {
  sendImmediately: false,
  type: 'Output',
}

InvoiceHook.propTypes = {
  children: PropTypes.func,
  type: PropTypes.oneOf(['Output', 'Input']),
  order: PropTypes.shape({
    orderId: PropTypes.string.isRequired,
  }),
  errorNodeSelector: PropTypes.string,
  sendImmediately: PropTypes.bool,
  device: PropTypes.shape({
    deviceId: PropTypes.string.isRequired,
    deviceName: PropTypes.string.isRequired,
  }),
  messages: PropTypes.shape({
    default: PropTypes.string.isRequired,
    success: PropTypes.string.isRequired,
    error: PropTypes.string.isRequired,
  }).isRequired,
  retries: PropTypes.number,
}

export default InvoiceHook
